// eslint-disable-next-line @typescript-eslint/no-unused-vars,import/no-unresolved
import { useLocation } from '@reach/router';
import { StaticImage } from 'gatsby-plugin-image';
import React from 'react';
import { graphql, useStaticQuery } from 'gatsby';
import { css } from '@emotion/react';
import SiteSearch from '../site-search/site-search';
import ResponderLogoIcon from '../../images/first-responder-logo.svg';
import IconFacebook from '../../images/svg/facebook.svg';
import IconInstagram from '../../images/svg/instagram.svg';
import IconLinkedIn from '../../images/svg/linkedin.svg';
import IconTwitter from '../../images/svg/twitter.svg';
import RichTextEditorStyles from '../utils/rich-text-editor-styles';
import IconYoutube from '../../images/svg/youtube.svg';
import {
    SOCIAL_URL_FACEBOOK,
    SOCIAL_URL_LINKEDIN,
    SOCIAL_URL_TWITTER,
    SOCIAL_URL_YOUTUBE,
} from '../../constants';
import { useMicrosite } from '../../contexts/franchise/microsite-context';
import FranchiseLicenses from '../franchise/microsite/licenses';
import Container from '../primitives/grid/container';
import { useDniProvider, DniBrand } from '../../contexts/dni-provider';
import Link from '../primitives/link';
import { colors } from '../primitives/colors';

const styles = {
    cursorDefault: css({
        cursor: 'default',
    }),
    autoPilot: css({
        '&.be-ix-link-block': {
            backgroundColor: colors.primaryOrange,
            display: 'flex',
            justifyContent: 'end',
            color: colors.shadesWhite,
            '.be-related-link-container': {
                padding: '24px 48px',
                display: 'flex',
                flexWrap: 'wrap',
                flexDirection: 'column',
                textAlign: 'right',
                '@media (min-width: 768px)': {
                    padding: '24px 32px',
                    gap: '6px 24px',
                    flexDirection: 'row',
                },
                '@media (min-width: 1024px)': {
                    padding: '16px 48px',
                },
                '.be-label': {
                    fontWeight: '600',
                    flexBasis: 'auto',
                    marginBottom: '4px',
                    '@media (min-width: 768px)': {
                        gap: '16px',
                        marginBottom: 0,
                    },
                },
                '.be-list': {
                    display: 'flex',
                    flexDirection: 'column',
                    flexWrap: 'wrap',
                    '@media (min-width: 768px)': {
                        gap: '0 16px',
                        flexDirection: 'row',
                    },
                },
            },
        },
    }),
    siteFooter: (isMicrosite: boolean) => css({
        position: 'relative',
        zIndex: 10,
        backgroundColor: colors.shadesWhite,
        borderTopWidth: '2px',
        borderColor: colors.primaryOrange,
        marginBottom: isMicrosite ? '36px' : '60px',
        '@media (min-width: 375px)': {
            marginBottom: isMicrosite ? '60px' : '92px',
        },
        '@media (min-width: 1024px)': {
            marginBottom: 0,
        },
    }),
    footerMobileInfo: css({
        maxWidth: '250px',
        marginBottom: '32px',
        '@media (min-width: 1024px)': {
            display: 'none',
        },
    }),
    widgetSection: css({
        position: 'relative',
        paddingTop: '36px',
        '@media (min-width: 1024px)': {
            paddingBottom: '28px',
            marginBottom: '32px',
        },
    }),
    staticDisclaimer: css({
        marginBottom: '24px',
        lineHeight: 1.375,
    }),
    dynamicDisclaimersWrap: css({
        fontSize: '14px',
        lineHeight: '20px',
    }),
    subfooter: css({
        paddingTop: '12px',
        paddingBottom: '12px',
        justifyContent: 'center',
        fontSize: '18px',
        lineHeight: '28px',
        '@media (min-width: 1024px)': {
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderTopWidth: '1px',
            fontSize: '14px',
            lineHeight: '20px',
            borderColor: colors.grey300,
        },
    }),
    subfooterCol: css({
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        '@media (min-width: 1024px)': {
            flex: '1 1 auto',
        },
    }),
    subfooterSocialLinks: css({
        display: 'flex',
        paddingBottom: '12px',
        paddingTop: '16px',
        justifyContent: 'space-between',
        alignItems: 'center',
        width: '100%',
        fontSize: '20px',
        lineHeight: '28px',
        '@media (min-width: 1024px)': {
            marginLeft: 'auto',
            maxWidth: 'max-content',
        },
        '>:not([hidden])~:not([hidden])': {
            marginLeft: '48px',
        },
    }),
    subfooterSocialLink: css({
        color: colors.primaryOrange,
        svg: {
            fill: colors.primaryOrange,
        },
    }),
    subfooterSocialLinksMicrosite: css({
        display: 'flex',
        paddingTop: '12px',
        paddingBottom: '12px',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: colors.primaryOrange,
        '>:not([hidden])~:not([hidden])': {
            marginLeft: '32px',
        },
    }),
    subfooterSocialLinkLight: css({
        color: colors.shadesWhite,
        svg: {
            fill: colors.shadesWhite,
        },
    }),
    colophon: css({
        width: '100%',
        textAlign: 'center',
        '@media (min-width: 1024px)': {
            paddingTop: '16px',
            paddingBottom: '16px',
            textAlign: 'left',
            width: 'auto',
        },
    }),
    navImageOuterWrap: css({
        display: 'flex',
        right: '0',
        bottom: '0',
        left: '0',
        paddingTop: '8px',
        paddingBottom: '8px',
    }),
    navImageInnerWrap: css({
        display: 'flex',
        paddingBottom: '8px',
        marginTop: '8px',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%',
        '@media (min-width: 640px)': {
            paddingBottom: '0',
            marginTop: '0',
            flexDirection: 'row',
            '>:not([hidden])~:not([hidden])': {
                marginLeft: '32px',
            },
        },
        '@media (min-width: 1024px)': {
            justifyContent: 'flex-end',
        },
    }),
    searchbarWrapper: css({
        maxWidth: '325px',
        marginBottom: '48px',
        fontSize: '18px',
        lineHeight: '28px',
        '@media (min-width: 1024px)': {
            display: 'none',
            paddingRight: '0',
        },
    }),
    linksColumns: (isMicrosite: boolean) => css({
        display: 'grid',
        gridTemplateColumns: 'repeat(1, minmax(0, 1fr))',
        alignItems: 'flex-start',
        gridAutoFlow: 'dense',
        '@media (min-width: 768px)': {
            gridTemplateColumns: '1fr 1fr 1fr',
            flexDirection: 'row',
            flexWrap: 'wrap',
            justifyContent: 'space-between',
        },
        '@media (min-width: 1024px)': {
            gridTemplateColumns: isMicrosite ? '1fr 1fr 1fr 1fr' : '2fr 1fr 1fr 1fr',
            gridTemplateRows: '1fr 2fr',
        },
    }),
    linksColumn: (penultItem?: boolean, lastItem?: boolean) => css({
        marginBottom: '20px',
        '@media (min-width: 1024px)': {
            marginBottom: '0',
            gridRow: (() => {
                if (lastItem) return '2 / 3';
                if (penultItem) return '1 / 2';
                return '1 / 3';
            })(),
            gridColumn: (lastItem || penultItem) ? '4 / 5' : undefined,
        },
    }),
    navTitle: css({
        paddingBottom: '4px',
        marginBottom: '8px',
        width: '160px',
        fontSize: '18px',
        lineHeight: '28px',
        fontWeight: 500,
        color: colors.primaryOrange,
        '@media (min-width: 1024px)': {
            borderBottomWidth: '1px',
            borderColor: colors.primaryOrange,
            fontSize: '21px',
        },
    }),
    navContainer: css({
        display: 'flex',
        '@media (min-width: 1024px)': {
            flexDirection: 'row',
        },
    }),
    navLinks: (doubleWidth: boolean) => css({
        display: 'flex',
        flexDirection: 'column',
        gap: !doubleWidth ? '0' : undefined,
        marginRight: '12px',
        marginBottom: '16px',
        gridAutoFlow: 'column',
        gridTemplateRows: 'repeat(15, min-content)',
        '@media (min-width: 375px)': {
            width: '100%',
            marginBottom: '0',
        },
        '@media (min-width: 1024px)': {
            display: 'grid',
            gridTemplateColumns: doubleWidth ? 'repeat(2, minmax(0, 1fr))' : 'repeat(1, minmax(0, 1fr))',
        },
    }),
    navLinksItem: css({
        marginBottom: '12px',
        '.nav-links': {
            background: 'red',
            paddingLeft: '12px',
        },
    }),
};

type Sublink = { title?: string, url?: string, className?: string, sublinks?: Sublink[] };

const Footer = (): JSX.Element => {
    const location = useLocation();

    const {
        isMicrosite,
        footer,
        franchise,
    } = useMicrosite();

    const { provider: DniProvider } = useDniProvider();
    const servproUrlRegex = /^(http(s?):\/\/)?(www\.)?servpro\.com\/?$/;
    const {
        allCsFooterDisclaimers: {
            nodes: dynamicFooterDisclaimers,
        },
        csLegal,
        csFooterNav: defaultFooter,
    } = useStaticQuery<Queries.FooterAncillaryDataQuery>(
        graphql`
            query FooterAncillaryData {
                csLegal {
                    copy
                }
                allCsFooterDisclaimers {
                    nodes {
                        contentBefore
                        content
                        pageMatch
                    }
                }
                csFooterNav {
                  title
                  links {
                    title
                    url
                    className
                    links {
                      title
                      url
                      className
                      sublinks {
                        title
                        url
                        sublinks {
                          title
                          url
                        }
                      }
                    }
                  }
                }
            }
        `,
    );

    const staticDisclaimerCopy = csLegal?.copy || '';
    const franchiseLinks = footer?.links;
    const links = isMicrosite ? franchiseLinks : (defaultFooter?.links || []);

    return (
        <div>
            {isMicrosite && (
                <div css={styles.subfooterSocialLinksMicrosite}>
                    {franchise?.socialMedia?.linkedIn && (
                        <Link css={styles.subfooterSocialLinkLight} to={`https://linkedin.com/${franchise?.socialMedia?.linkedIn}`} target="_blank">
                            <IconLinkedIn />
                        </Link>
                    )}
                    {franchise?.socialMedia?.twitter && (
                        <Link css={styles.subfooterSocialLinkLight} to={`https://twitter.com/${franchise?.socialMedia?.twitter}`} target="_blank">
                            <IconTwitter />
                        </Link>
                    )}
                    {franchise?.socialMedia?.instagram && (
                        <Link css={styles.subfooterSocialLinkLight} to={`https://instagram.com/${franchise?.socialMedia?.instagram}`} target="_blank">
                            <IconInstagram />
                        </Link>
                    )}
                    {franchise?.socialMedia?.facebook && (
                        <Link css={styles.subfooterSocialLinkLight} to={`https://facebook.com/${franchise?.socialMedia?.facebook}`} target="_blank">
                            <IconFacebook />
                        </Link>
                    )}
                </div>
            )}
            {!isMicrosite && <div css={styles.autoPilot} className="be-ix-link-block" />}
            <footer css={styles.siteFooter(isMicrosite)}>
                <Container>
                    <section css={styles.widgetSection} id="main-footer--nav">
                        {!isMicrosite && (
                            <>
                                <p css={styles.footerMobileInfo}>
                                    801 Industrial Blvd
                                    <br />
                                    Gallatin, TN 37066
                                    <br />
                                </p>
                                <div css={styles.searchbarWrapper}>
                                    <SiteSearch />
                                </div>
                            </>
                        )}
                        <div css={styles.linksColumns(isMicrosite)}>
                            {links?.map((group, id) => {
                                if (!group) return null;
                                const doubleWidth = !isMicrosite && id === 0;
                                const lastItem = !isMicrosite && id === links.length - 1;
                                const penultItem = !isMicrosite && id === links.length - 2;

                                // Only do it for this level, this level is for the 'headings'
                                if (!group.links || group.links.filter(x => !!x).length === 0) return null;

                                return (
                                    <div css={styles.linksColumn(penultItem, lastItem)} key={group.title}>
                                        <p css={styles.navTitle}>{group.title}</p>
                                        <div css={styles.navContainer}>
                                            <ul css={styles.navLinks(doubleWidth)}>
                                                {group.links.filter(link => DniProvider !== DniBrand.TELMETRICS || (DniProvider === DniBrand.TELMETRICS && !servproUrlRegex.test(link?.url || ''))).map(
                                                    (groupLink) => {
                                                        if (!groupLink) return null;

                                                        const { title, url } = groupLink;
                                                        const sublinks = (('sublinks' in groupLink && Array.isArray(groupLink.sublinks)) ? groupLink?.sublinks : []) as Sublink[];
                                                        const className = groupLink?.className || '';

                                                        return (
                                                            <React.Fragment key={title}>
                                                                <li css={styles.navLinksItem}>
                                                                    {url ? (
                                                                        <Link to={url} className={className}>
                                                                            {title}
                                                                        </Link>
                                                                    ) : (
                                                                        <p css={styles.cursorDefault} className={className}>
                                                                            {title}
                                                                        </p>
                                                                    )}
                                                                </li>
                                                                {sublinks.length > 0 && sublinks.map((sublink) => {
                                                                    const {
                                                                        title: sublinkTitle,
                                                                        url: sublinkUrl,
                                                                    } = sublink;

                                                                    const nestedSublinks = sublink?.sublinks || [];
                                                                    const subClassName = sublink.className;

                                                                    return (
                                                                        <React.Fragment key={sublinkTitle}>
                                                                            <li css={[styles.navLinksItem, { '@media (min-width: 375px)': { marginLeft: '12px' } }]}>
                                                                                {sublinkUrl ? (
                                                                                    <Link to={sublinkUrl} className={subClassName}>
                                                                                        {sublinkTitle}
                                                                                    </Link>
                                                                                ) : (
                                                                                    <p
                                                                                        css={styles.cursorDefault}
                                                                                        className={subClassName}
                                                                                    >
                                                                                        {sublinkTitle}
                                                                                    </p>
                                                                                )}
                                                                            </li>
                                                                            {nestedSublinks.length > 0 && nestedSublinks.map(
                                                                                (nestedSubLink) => {
                                                                                    const {
                                                                                        title: nestedSublinkTitle,
                                                                                        url: nestedSublinkUrl,
                                                                                    } = nestedSubLink;

                                                                                    const nestedClassName = nestedSubLink.className;

                                                                                    return (
                                                                                        <li
                                                                                            css={[styles.navLinksItem, { '@media (min-width: 375px)': { marginLeft: '8px' } }]}
                                                                                            key={nestedSublinkTitle}
                                                                                        >
                                                                                            {nestedSublinkUrl ? (
                                                                                                <Link
                                                                                                    to={nestedSublinkUrl}
                                                                                                    className={nestedClassName}
                                                                                                >
                                                                                                    {nestedSublinkTitle}
                                                                                                </Link>
                                                                                            ) : (
                                                                                                <p
                                                                                                    css={styles.cursorDefault}
                                                                                                    className={
                                                                                                        nestedClassName
                                                                                                    }
                                                                                                >
                                                                                                    {nestedSublinkTitle}
                                                                                                </p>
                                                                                            )}
                                                                                        </li>
                                                                                    );
                                                                                },
                                                                            )}
                                                                        </React.Fragment>
                                                                    );
                                                                })}
                                                            </React.Fragment>
                                                        );
                                                    },
                                                )}
                                            </ul>
                                        </div>
                                    </div>
                                );
                            })}
                            {isMicrosite && (
                                <div css={styles.linksColumn()}>
                                    <FranchiseLicenses />
                                </div>
                            )}
                            {isMicrosite && (
                                <address css={[
                                    styles.linksColumn(),
                                    {
                                        fontStyle: 'normal',
                                        display: 'block',
                                    },
                                ]}
                                >
                                    {!franchise?.addressHidden && (
                                        <>
                                            <strong>{franchise?.name}</strong>
                                            <br />
                                            {!!franchise?.address.address && (
                                                <>
                                                    {franchise.address.address}
                                                    <br />
                                                </>
                                            )}
                                            {!!franchise?.address.addressExt && (
                                                <>
                                                    {franchise.address.addressExt}
                                                    <br />
                                                </>
                                            )}
                                            {franchise?.address?.city}
                                            {', '}
                                            {franchise?.address?.state}
                                            {' '}
                                            {franchise?.address?.postalCode}
                                        </>
                                    )}
                                </address>
                            )}
                        </div>
                        <div css={styles.navImageOuterWrap}>
                            <div css={styles.navImageInnerWrap}>
                                <StaticImage
                                    width={300}
                                    alt="Certificates"
                                    formats={['auto', 'webp']}
                                    src="../../images/certificates.png"
                                />
                                <ResponderLogoIcon />
                            </div>
                        </div>
                    </section>

                    {!!staticDisclaimerCopy && (
                        <section css={styles.dynamicDisclaimersWrap}>
                            <div
                                css={[RichTextEditorStyles, styles.staticDisclaimer]}
                                /* eslint-disable-next-line react/no-danger */
                                dangerouslySetInnerHTML={{ __html: staticDisclaimerCopy || '' }}
                            />
                        </section>
                    )}
                    <section css={styles.dynamicDisclaimersWrap}>
                        {dynamicFooterDisclaimers
                            .filter(fd => fd?.pageMatch === location?.pathname)
                            .map(fd => (
                                <React.Fragment key={fd.content}>
                                    {!!fd?.contentBefore && (
                                        <div
                                            css={[RichTextEditorStyles, styles.staticDisclaimer]}
                                            /* eslint-disable-next-line react/no-danger */
                                            dangerouslySetInnerHTML={{ __html: fd.contentBefore || '' }}
                                        />
                                    )}
                                    {!!fd?.content && (
                                        <div
                                            css={[RichTextEditorStyles, styles.staticDisclaimer]}
                                            /* eslint-disable-next-line react/no-danger */
                                            dangerouslySetInnerHTML={{ __html: fd.content || '' }}
                                        />
                                    )}
                                </React.Fragment>
                            ))}
                    </section>
                    <section css={styles.subfooter} id="main-footer--subfooter">
                        <div css={styles.subfooterCol}>
                            <p css={styles.colophon}>
                                ©
                                {' '}
                                {new Date().getFullYear()}
                                {' '}
                                SERVPRO. All rights reserved.
                            </p>
                        </div>
                        {!isMicrosite && (
                            <div css={styles.subfooterCol}>
                                <ul css={styles.subfooterSocialLinks}>
                                    <Link css={styles.subfooterSocialLink} to={SOCIAL_URL_LINKEDIN} target="_blank">
                                        <IconLinkedIn />
                                    </Link>
                                    <Link css={styles.subfooterSocialLink} to={SOCIAL_URL_TWITTER} target="_blank">
                                        <IconTwitter />
                                    </Link>
                                    <Link css={styles.subfooterSocialLink} to={SOCIAL_URL_YOUTUBE} target="_blank">
                                        <IconYoutube />
                                    </Link>
                                    <Link css={styles.subfooterSocialLink} to={SOCIAL_URL_FACEBOOK} target="_blank">
                                        <IconFacebook />
                                    </Link>
                                </ul>
                            </div>
                        )}
                    </section>
                </Container>
            </footer>
        </div>
    );
};

export default Footer;
